import type Shipment from '~/types/Shipment';

import { type OrderSelection } from '~/types/Order';
import { type TrackingReference } from '~/utils/TrackingHelper/types';

import { ActionKeys } from './constants';

export interface FetchShipmentPayload {
  shipment: Shipment;
}

export const fetchShipment = (shipmentId: string, trackingReference: TrackingReference) =>
  <const>{
    shipmentId,
    trackingReference,
    type: ActionKeys.SHIPMENT_FETCH,
  };

export const fetchShipmentRatesSuccess = (
  shipmentId: string,
  payload: FetchShipmentPayload,
  trackingReference: TrackingReference,
) =>
  <const>{
    payload,
    shipmentId,
    trackingReference,
    type: ActionKeys.SHIPMENT_FETCH_SUCCESS,
  };

export const fetchShipmentRatesError = (shipmentObjectId: string) =>
  <const>{
    payload: {
      shipmentObjectId,
    },
    type: ActionKeys.SHIPMENT_FETCH_ERROR,
  };

export const recalculateHiddenRates = ({ shipmentObjectIds }: { shipmentObjectIds?: string[] }) =>
  <const>{
    payload: {
      shipmentObjectIds,
    },
    type: ActionKeys.SHIPMENT_RECALCULATE_HIDDEN_RATES,
  };

export const selectRate = (
  shipmentObjectId: string,
  rateObjectId: string,
  userTriggered = true,
  trackingReference: TrackingReference,
  orderSelection?: OrderSelection,
  serviceLevelToken?: string,
  accountObjectId?: string,
) =>
  <const>{
    payload: {
      accountObjectId,
      orderSelection,
      rateObjectId,
      serviceLevelToken,
      shipmentObjectId,
      trackingReference,
      userTriggered,
    },
    type: ActionKeys.RATE_SELECT,
  };

export const selectRates = (
  orderSelection: OrderSelection,
  rateSelection: HashMap<string, string>,
  serviceLevelToken: string,
  serviceLevelKey: string,
  shouldIgnoreCarrier: boolean,
  trackingReference: TrackingReference,
  accountObjectId?: string,
) =>
  <const>{
    payload: {
      accountObjectId,
      orderSelection,
      rateSelection,
      serviceLevelKey,
      serviceLevelToken,
      shouldIgnoreCarrier,
      trackingReference,
    },
    type: ActionKeys.MULTIPLE_RATE_SELECT,
  };

export const setHiddenRates = (hiddenRates: string[]) =>
  <const>{
    payload: {
      hiddenRates,
    },
    type: ActionKeys.RATE_SET_HIDDEN_RATES,
  };

export const appendHiddenRates = (hiddenRates: string[]) =>
  <const>{
    payload: {
      hiddenRates,
    },
    type: ActionKeys.RATE_APPEND_HIDDEN_RATES,
  };

export const clearRateSelection = () =>
  <const>{
    type: ActionKeys.RATE_CLEAR_SELECTION,
  };

export const clearRates = (shipmentIds: string[]) =>
  <const>{
    payload: {
      shipmentIds,
    },
    type: ActionKeys.RATE_CLEAR,
  };

export type ShipmentsActions =
  | ReturnType<typeof appendHiddenRates>
  | ReturnType<typeof clearRateSelection>
  | ReturnType<typeof clearRates>
  | ReturnType<typeof fetchShipment>
  | ReturnType<typeof fetchShipmentRatesError>
  | ReturnType<typeof fetchShipmentRatesSuccess>
  | ReturnType<typeof recalculateHiddenRates>
  | ReturnType<typeof selectRate>
  | ReturnType<typeof selectRates>
  | ReturnType<typeof setHiddenRates>;
